import * as React from 'react';
import {
  MenuTrigger,
  Menu,
  MenuItem,
  Popover,
  Button as RACButton,
  Separator,
} from 'react-aria-components';
import { DownSmallLine, CheckLine, AddLine } from '@mingcute/react';

import { cn } from '../lib/cn';

/**
 * Klaret WorkspaceSwitcher (handoff §10.3 Select / §10.8 Menu).
 * Trigger = avatar + name + chevron. Popover list = RAC MenuTrigger/Menu/MenuItem.
 * No Radix, no lucide.
 */

export interface Workspace {
  id: string;
  name: string;
  avatarUrl?: string;
  initials?: string;
  subtitle?: string;
}

export interface WorkspaceSwitcherProps {
  workspaces: Workspace[];
  currentId: string;
  onSelect: (id: string) => void;
  onCreate?: () => void;
  createLabel?: string;
  className?: string;
}

function deriveInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
}

const WorkspaceAvatar: React.FC<{ ws: Workspace; size?: number }> = ({ ws, size = 24 }) => {
  const initials = ws.initials ?? deriveInitials(ws.name);
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-[var(--tvl-r2)]',
        'bg-[var(--tvl-sunken)] text-[var(--tvl-ink-2)]',
        'text-[10px] font-[600] leading-none',
      )}
      style={{ width: size, height: size }}
    >
      {ws.avatarUrl ? (
        <img src={ws.avatarUrl} alt={ws.name} className="h-full w-full object-cover" />
      ) : (
        initials || '?'
      )}
    </span>
  );
};

const popoverClass = cn(
  'z-[var(--tvl-z-popover)] min-w-[240px]',
  'rounded-[var(--tvl-r3)] border border-[var(--tvl-hairline)]',
  'bg-[var(--tvl-raised)] p-1 shadow-[var(--tvl-e2)]',
  'outline-none',
  'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
  'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95',
  'placement-bottom:slide-in-from-top-1',
);

const menuItemClass = cn(
  'flex cursor-pointer items-center gap-2 rounded-[var(--tvl-r2)] px-2 py-1.5',
  'text-[14px] leading-[20px] text-[var(--tvl-ink)] outline-none',
  'hover:bg-[var(--tvl-wash-hover)] focus:bg-[var(--tvl-wash-hover)]',
  'pressed:bg-[var(--tvl-wash-pressed)]',
);

export const WorkspaceSwitcher: React.FC<WorkspaceSwitcherProps> = ({
  workspaces,
  currentId,
  onSelect,
  onCreate,
  createLabel = 'Create workspace',
  className,
}) => {
  const current = workspaces.find((w) => w.id === currentId) ?? workspaces[0];
  if (!current) return null;

  const handleAction = (key: React.Key) => {
    const id = String(key);
    if (id === '__create__') {
      onCreate?.();
    } else {
      onSelect(id);
    }
  };

  return (
    <MenuTrigger>
      <RACButton
        aria-label="Switch workspace"
        className={cn(
          'flex items-center gap-2 rounded-[var(--tvl-r2)] px-2 py-1.5',
          'text-[14px] font-[500] leading-[20px] text-[var(--tvl-ink)]',
          'hover:bg-[var(--tvl-wash-hover)] pressed:bg-[var(--tvl-wash-pressed)]',
          'outline-none',
          'focus-visible:outline-[2px] focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
          'min-w-0',
          className,
        )}
      >
        <WorkspaceAvatar ws={current} />
        <span className="max-w-[140px] truncate">{current.name}</span>
        <DownSmallLine size={16} color="var(--tvl-ink-3)" aria-hidden />
      </RACButton>

      <Popover placement="bottom start" offset={4} className={popoverClass}>
        <Menu onAction={handleAction} className="outline-none">
          {/* Section label */}
          <div className="px-2 py-1.5 text-[11px] font-[500] uppercase tracking-wider text-[var(--tvl-ink-3)]">
            Workspaces
          </div>

          {workspaces.map((ws) => (
            <MenuItem key={ws.id} id={ws.id} className={menuItemClass}>
              <WorkspaceAvatar ws={ws} />
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-[14px] leading-[20px] text-[var(--tvl-ink)]">
                  {ws.name}
                </span>
                {ws.subtitle ? (
                  <span className="truncate text-[12px] leading-[16px] text-[var(--tvl-ink-3)]">
                    {ws.subtitle}
                  </span>
                ) : null}
              </div>
              {ws.id === currentId ? (
                <CheckLine size={16} color="var(--tvl-accent)" aria-hidden />
              ) : null}
            </MenuItem>
          ))}

          {onCreate ? (
            <>
              <Separator className="my-1 h-px bg-[var(--tvl-hairline)]" />
              <MenuItem
                id="__create__"
                className={cn(menuItemClass, 'text-[var(--tvl-accent)]')}
              >
                <AddLine size={16} color="var(--tvl-accent)" aria-hidden />
                {createLabel}
              </MenuItem>
            </>
          ) : null}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
};
WorkspaceSwitcher.displayName = 'WorkspaceSwitcher';
